/**
 * THE N2 WEEKLY CHECK-IN, AS THE COACH READS IT — 2026-10-08 (`docs/coach/PRINCIPLES.md` N2, §14.1).
 *
 * The athlete answers on the phone (sleep hours a night, appetite 1–5 against usual, desire to train
 * 1–5, a life-event flag with a note) and may tag a weigh-in with why it carries water. This file
 * turns one `body_measurements` row into the lines a coach reads. It is a function so a test drives
 * it; the panel only draws what it returns.
 *
 * ⚠️ LABELS COME FROM THE GENERATED VOCABULARY (`checkInFields.ts`), never typed here — the same
 * words the athlete saw on the phone, or two words for one question.
 * ⚠️ NULL IS "NOT ANSWERED" AND DRAWS NOTHING. Never a zero, never a "No": an absent sleep is not 0 h,
 * and a life event the coach did not ask about is not "no life event".
 * ⚠️ A FLAG IS A QUESTION, NEVER A SENTENCE (N2): the panel says what was answered, it does not judge it.
 */
import { CHECK_IN_FIELDS, waterTagLabels } from "./checkInFields";
import { wireNumber } from "./wireNumber";

/** The columns this reader needs, as one select list — the panel's query names exactly these. */
export const WEEK_COLUMNS =
  "sleep_hours, appetite, training_desire, life_event, life_event_note, water_tags" as const;

export type WeekRow = {
  sleep_hours?: unknown;
  appetite?: unknown;
  training_desire?: unknown;
  life_event?: boolean | null;
  life_event_note?: string | null;
  water_tags?: string[] | null;
};

export type WeekLine = { readonly key: string; readonly label: string; readonly value: string };

function field(key: string) {
  const f = CHECK_IN_FIELDS.find((x) => x.key === key);
  if (f === undefined) throw new Error(`check-in vocabulary has no ${key}`);
  return f;
}

function scale(key: string, raw: unknown): string | null {
  const v = wireNumber(raw);
  if (v === null || !Number.isInteger(v) || v < 1 || v > 5) return null;
  const ends = field(key).scaleEnds;
  if (ends !== undefined && v === 1) return `1 of 5 · ${ends[0]}`;
  if (ends !== undefined && v === 5) return `5 of 5 · ${ends[1]}`;
  return `${v} of 5`;
}

/** The answered week questions, in the vocabulary's order. Empty when nothing was answered. */
export function weekAnswerLines(row: WeekRow): WeekLine[] {
  const out: WeekLine[] = [];
  const sleep = wireNumber(row.sleep_hours);
  if (sleep !== null) out.push({ key: "sleepHours", label: field("sleepHours").label, value: `${sleep.toFixed(1)} h a night` });
  const appetite = scale("appetite", row.appetite);
  if (appetite !== null) out.push({ key: "appetite", label: field("appetite").label, value: appetite });
  const desire = scale("trainingDesire", row.training_desire);
  if (desire !== null) out.push({ key: "trainingDesire", label: field("trainingDesire").label, value: desire });
  if (row.life_event === true || row.life_event === false) {
    const note = row.life_event === true && row.life_event_note ? ` — ${row.life_event_note}` : "";
    out.push({ key: "lifeEvent", label: field("lifeEvent").label, value: (row.life_event ? "Yes" : "No") + note });
  }
  return out;
}

/** The weigh-in's water tag(s) as words, or null when there are none — beside the weight, never alone. */
export function waterTagText(row: WeekRow): string | null {
  const labels = waterTagLabels(row.water_tags);
  return labels.length === 0 ? null : labels.join(", ");
}
